import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Plus, LayoutDashboard, Car, Route, FileText, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';

function AdminSchedules() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    route_id: '',
    vehicle_id: '',
    departure_time: '',
    arrival_time: '',
    date: '',
    price: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, vehiclesRes, routesRes] = await Promise.all([
        api.get('/admin/schedules'),
        api.get('/admin/vehicles'),
        api.get('/admin/routes'),
      ]);
      setSchedules(schedulesRes.data);
      setVehicles(vehiclesRes.data);
      setRoutes(routesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.route_id || !formData.vehicle_id || !formData.departure_time || !formData.arrival_time || !formData.date || !formData.price) {
      toast.error('Please fill all fields');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/schedules', {
        ...formData,
        price: parseFloat(formData.price),
      });
      toast.success('Schedule created successfully');
      setFormData({ route_id: '', vehicle_id: '', departure_time: '', arrival_time: '', date: '', price: '' });
      setOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex">
      <div className="w-64 bg-primary text-white p-6">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
            alt="Hasaka Tours & Travels"
            className="h-16 w-auto mb-3 brightness-0 invert"
          />
          <h2 className="font-manrope font-bold text-xl">Admin Panel</h2>
        </div>
        <nav className="space-y-2">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/vehicles" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Car className="w-5 h-5" />
            Vehicles
          </Link>
          <Link to="/admin/routes" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Route className="w-5 h-5" />
            Routes
          </Link>
          <Link to="/admin/schedules" className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary-900 text-white font-medium">
            <CalendarIcon className="w-5 h-5" />
            Schedules
          </Link>
          <Link to="/admin/bookings" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <FileText className="w-5 h-5" />
            Bookings
          </Link>
        </nav>
        <div className="mt-auto pt-12">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full mb-3 border-white text-white hover:bg-white hover:text-primary rounded-sm">
            User Dashboard
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-white hover:bg-primary-900 rounded-sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex-1 p-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="schedules-heading">Schedules Management</h1>
            <p className="text-slate-600 font-public-sans text-lg">Create and manage trip schedules</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-6 py-3" data-testid="add-schedule-button">
                <Plus className="w-5 h-5 mr-2" />
                Add Schedule
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl text-primary">Add New Schedule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Route</label>
                    <Select onValueChange={(value) => setFormData({ ...formData, route_id: value })} data-testid="route-select">
                      <SelectTrigger className="rounded-none border-slate-300 h-10">
                        <SelectValue placeholder="Select Route" />
                      </SelectTrigger>
                      <SelectContent>
                        {routes.map((route) => (
                          <SelectItem key={route.route_id} value={route.route_id}>
                            {route.origin} → {route.destination}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Vehicle</label>
                    <Select onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })} data-testid="vehicle-select">
                      <SelectTrigger className="rounded-none border-slate-300 h-10">
                        <SelectValue placeholder="Select Vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((vehicle) => (
                          <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                            {vehicle.vehicle_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Date</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      data-testid="date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Departure</label>
                    <Input
                      type="time"
                      value={formData.departure_time}
                      onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      data-testid="departure-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Arrival</label>
                    <Input
                      type="time"
                      value={formData.arrival_time}
                      onChange={(e) => setFormData({ ...formData, arrival_time: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      data-testid="arrival-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="rounded-none border-slate-300 h-10"
                    placeholder="50.00"
                    data-testid="price-input"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3 w-full"
                  data-testid="create-schedule-button"
                >
                  {creating ? 'Creating...' : 'Create Schedule'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading schedules...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => {
              const route = routes.find((r) => r.route_id === schedule.route_id);
              const vehicle = vehicles.find((v) => v.vehicle_id === schedule.vehicle_id);
              return (
                <div key={schedule.schedule_id} className="bg-white rounded-sm p-6 shadow-sm border border-slate-200" data-testid={`schedule-${schedule.schedule_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-manrope font-bold text-xl text-primary mb-3">
                        {route?.origin} → {route?.destination}
                      </h3>
                      <div className="grid grid-cols-5 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500 font-public-sans">Date:</span>
                          <p className="font-semibold text-primary">{schedule.date}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 font-public-sans">Departure:</span>
                          <p className="font-mono font-bold text-primary">{schedule.departure_time}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 font-public-sans">Arrival:</span>
                          <p className="font-mono font-bold text-primary">{schedule.arrival_time}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 font-public-sans">Vehicle:</span>
                          <p className="font-semibold text-primary">{vehicle?.vehicle_number}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 font-public-sans">Available:</span>
                          <p className="font-semibold text-secondary">{schedule.available_seats.length} seats</p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-6 text-right">
                      <p className="font-manrope text-3xl font-bold text-secondary">${schedule.price}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSchedules;
